import { useState } from 'react';
import { useMutation } from 'react-apollo';

import { CARD_SIDE, ORIENTATION } from '../../../services/cards/constants';
import useCardFetch from './useCardFetch';
import useCardElementCreate from './useCardElementCreate';
import useCardSettings from './useCardSettings';
import {
  cardElementUpdate as cardElementUpdateMutation,
  cardElementsUpdate as cardElementsUpdateMutation,
} from '../../../apps/cards/graphql/mutations/cardElement';
import getLogger from '../../../util/logger';
import useOpenAlert from '../../../hooks/useOpenAlert';
import { getErrorString } from '../../../util/normalize-errors';
import useCardDesignerCardElementConfiguration from './useCardDesignerCardElementConfiguration';

const logger = getLogger('use-card-designer');

const useCardDesigner = ({
  defaultCardId,
  defaultCardSideId,
  defaultCardOrientation = ORIENTATION.LANDSCAPE,
  side = CARD_SIDE.FRONT,
  designMode,
  printPartnerId,
} = {}) => {
  const { loadingCard, card, refetchCard, cardSide } = useCardFetch({
    defaultCardId,
    defaultCardSideId,
    defaultCardOrientation,
    side,
    designMode,
    printPartnerId,
  });
  const openAlert = useOpenAlert();
  const [selectedCardElementId, setSelectedCardElementId] = useState(null);
  const onCardElementCreate = useCardElementCreate({
    card,
    cardSide,
    cardTemplate: cardSide?.card_template,
    setSelectedCardElementId,
  });
  const {
    onCardSettingsUpdate,
    loadingCardSettings,
    cardTemplateUpdate,
  } = useCardSettings();
  const {
    onElementConfigurationChange,
  } = useCardDesignerCardElementConfiguration({ card });
  const onCardElementSelect = cardElementId => {
    setSelectedCardElementId(cardElementId);
  };

  const [cardElementUpdate] = useMutation(cardElementUpdateMutation);
  const [cardElementsUpdate] = useMutation(cardElementsUpdateMutation);
  const onCardElementUpdate = async ({ cardElement, data }) => {
    const getUpdatedData = () => {
      if (data.z_index) {
        return {
          ...cardElement,
          z_index: data.z_index,
        };
      }

      if (data.frame) {
        return {
          ...cardElement,
          frame: {
            ...data.frame,
            __typename: 'CardElementFrame',
          },
        };
      }

      return cardElement;
    };

    try {
      await cardElementUpdate({
        variables: {
          input: {
            card_element_id: cardElement.id,
            ...data,
          },
        },
        optimisticResponse: {
          __typename: 'Mutation',
          cardElementUpdate: {
            card_element: {
              ...getUpdatedData(),
              __typename: 'CardElement',
            },
            __typename: 'CardElementUpdatePayload',
          },
        },
      });
    } catch (error) {
      logger.error(error);
      openAlert({
        message: 'An unexpected error occurred.',
        info: getErrorString(error),
      });
    }
  };
  const onCardElementsUpdate = async ({ cardElements, data }) => {
    const getUpdatedData = () => {
      return cardElements.map(cardElement => {
        if (data[cardElement.id].z_index) {
          return {
            ...cardElement,
            z_index: data[cardElement.id].z_index,
            __typename: 'CardElement',
          };
        }

        if (data[cardElement.id].frame) {
          return {
            ...cardElement,
            frame: {
              ...data[cardElement.id].frame,
              __typename: 'CardElementFrame',
            },
            __typename: 'CardElement',
          };
        }

        return { ...cardElement, __typename: 'CardElement' };
      });
    };

    try {
      await cardElementsUpdate({
        variables: {
          input: {
            card_elements: cardElements.map(elem => ({
              card_element_id: elem.id,
              ...data[elem.id],
            })),
          },
        },
        optimisticResponse: {
          __typename: 'Mutation',
          cardElementsUpdate: {
            success: true,
            card_elements: [...getUpdatedData()],
            __typename: 'CardElementsUpdatePayload',
          },
        },
      });
    } catch (error) {
      logger.error(error);
      openAlert({
        message: 'An unexpected error occurred.',
        info: getErrorString(error),
      });
    }
  };

  const onCardElementDelete = async cardElementId => {
    const cardElementIds = cardSide.card_template.card_elements
      .map(cardElement => cardElement.id)
      .filter(id => id !== cardElementId);

    try {
      await cardTemplateUpdate({
        variables: {
          input: {
            card_template_id: cardSide.card_template.id,
            card_element_ids: cardElementIds,
          },
        },
      });
    } catch (error) {
      logger.error(error);
      openAlert({
        message: 'An unexpected error occurred.',
        info: getErrorString(error),
      });
    }
  };

  return {
    card,
    refetchCard,
    cardSide,
    selectedCardElementId,
    onCardElementSelect,
    onCardElementCreate,
    onCardElementUpdate,
    onCardElementsUpdate,
    onCardElementDelete,
    loadingCard,
    onCardSettingsUpdate,
    loadingCardSettings,
    onElementConfigurationChange,
  };
};

export default useCardDesigner;
